import { toast } from '@/components/toast';
import { isGroupDraftEditable } from '@/pages/translation/utils';
import { logger } from '@/services/logger';
import { useGetTranslationGroup } from '@/state/translations';
import { CaptionsData } from '@d-id/types';
import styled from '@emotion/styled';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCurrentDraftGroupId } from '../../hooks/useCurrentDraftGroupId';
import { EDITOR_HEADER_HEIGHT } from '../constants';
import CaptionListItem, { CaptionListItemSkeleton } from './CaptionListItem';

const ListContainer = styled.ul`
    padding: 0.8rem 3rem;
    height: calc(100% - ${EDITOR_HEADER_HEIGHT});
    overflow-y: auto;
`;

function CaptionList() {
    const groupId = useCurrentDraftGroupId();
    const { data: group } = useGetTranslationGroup(groupId!);
    const [sourceCaptions, setSourceCaptions] = useState<CaptionsData | null>(null);

    const { watch } = useFormContext();

    const selectedTranslationId = watch('selectedTranslationId');
    const translatedSentences = watch(`captionsByTranslationId.${selectedTranslationId}.sentences`);

    const draftEditable = group && isGroupDraftEditable(group);

    const handleTranscriptionError = (err: any) => {
        logger.error('failed to fetch transcript file', err);
        toast.error({ title: 'Failed to load transcription, try again later' });
    };

    useEffect(() => {
        if (draftEditable) {
            axios
                .get(group.subtitles_url)
                .then(({ data }) => {
                    setSourceCaptions(data);
                })
                .catch(handleTranscriptionError);
        } else {
            setSourceCaptions(null);
        }
    }, [draftEditable]);

    if (!sourceCaptions || !translatedSentences) {
        return (
            <ListContainer>
                {new Array(3).fill(0).map((_, i) => (
                    <CaptionListItemSkeleton key={i} />
                ))}
            </ListContainer>
        );
    }

    return (
        <ListContainer>
            {sourceCaptions.sentences.map(({ text: originText, ...srcCaption }, index) => {
                const caption = { ...srcCaption, originText, translatedText: translatedSentences[index].text };
                const key = `${selectedTranslationId}-${srcCaption.start}-${srcCaption.end}`;
                return <CaptionListItem key={key} index={index} caption={caption} />;
            })}
        </ListContainer>
    );
}

export default CaptionList;
